import { useState } from 'react';
import { Send, Save, Paperclip, Trash2, AlertCircle } from 'lucide-react';
import { CONFIGS_BRIEFING, TipoProjeto } from '../config/briefingConfig';

type CampoValue = string | string[];

interface BriefingFormProps {
  tipo?: TipoProjeto;
  onSubmit: (dados: Record<string, any>, anexos: Record<string, File | null>) => Promise<void>;
  onSave?: (dados: Record<string, any>, anexos: Record<string, File | null>) => Promise<void>;
  submitting?: boolean;
  saving?: boolean;
  initialValues?: Record<string, any>;
}

export function BriefingForm({ tipo = 'SAAS_PRAGAS', onSubmit, onSave, submitting = false, saving = false, initialValues }: BriefingFormProps) {
  const config = CONFIGS_BRIEFING[tipo] || CONFIGS_BRIEFING.SAAS_PRAGAS;
  const secoes = config.secoes;
  const camposAnexos = config.anexos;
  const [values, setValues] = useState<Record<string, CampoValue>>(() => {
    const init: Record<string, CampoValue> = {};
    if (initialValues) {
      for (const [chave, valor] of Object.entries(initialValues)) {
        if (chave === '_anexos') continue;
        if (Array.isArray(valor)) init[chave] = valor.map(String);
        else if (typeof valor === 'string') init[chave] = valor;
      }
    }
    return init;
  });
  const [anexos, setAnexos] = useState<Record<string, File | null>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleTextChange = (id: string, value: string) => {
    setValues((prev) => ({ ...prev, [id]: value }));
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: '' }));
  };

  const handleChecklistChange = (id: string, opcao: string, checked: boolean) => {
    setValues((prev) => {
      const atual = (prev[id] as string[]) || [];
      const novo = checked ? [...atual, opcao] : atual.filter((o) => o !== opcao);
      return { ...prev, [id]: novo };
    });
  };

  const handleAnexoChange = (id: string, file: File | null) => {
    setAnexos((prev) => ({ ...prev, [id]: file }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    for (const secao of secoes) {
      for (const campo of secao.campos) {
        if (!campo.obrigatorio) continue;
        const valor = values[campo.id];
        if (!valor || (typeof valor === 'string' && !valor.trim())) {
          newErrors[campo.id] = `${campo.label} é obrigatório`;
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = (): { dados: Record<string, any>; anexosPreenchidos: Record<string, File | null> } => {
    const dados: Record<string, any> = { ...values };
    const anexosPreenchidos: Record<string, File | null> = {};
    const anexosInfo: Record<string, string> = {};

    for (const campo of camposAnexos) {
      const file = anexos[campo.id];
      if (file) {
        anexosPreenchidos[campo.id] = file;
        anexosInfo[campo.id] = file.name;
      }
    }
    if (Object.keys(anexosInfo).length > 0) {
      dados['_anexos'] = anexosInfo;
    }

    return { dados, anexosPreenchidos };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const { dados, anexosPreenchidos } = buildPayload();
    await onSubmit(dados, anexosPreenchidos);
  };

  const handleSave = async () => {
    if (!onSave) return;
    const { dados, anexosPreenchidos } = buildPayload();
    await onSave(dados, anexosPreenchidos);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {secoes.map((secao) => (
        <div key={secao.titulo} className="space-y-4">
          <div className="border-b border-border/50 pb-2">
            <h3 className="text-lg font-semibold text-foreground">{secao.titulo}</h3>
            {secao.descricao && (
              <p className="text-sm text-muted-foreground">{secao.descricao}</p>
            )}
          </div>

          <div className="space-y-4">
            {secao.campos.map((campo) => {
              const erro = errors[campo.id];

              if (campo.tipo === 'checklist') {
                const selecionadas = (values[campo.id] as string[]) || [];
                return (
                  <div key={campo.id} className="space-y-2">
                    <label className="block text-sm font-medium text-foreground">
                      {campo.label}
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {campo.opcoes?.map((opcao) => (
                        <label
                          key={opcao}
                          className="flex items-center gap-2 px-3 py-2 bg-background/50 border border-border/50 rounded-lg cursor-pointer hover:border-primary/50 transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selecionadas.includes(opcao)}
                            onChange={(e) => handleChecklistChange(campo.id, opcao, e.target.checked)}
                            className="h-4 w-4 rounded border-border accent-primary"
                          />
                          <span className="text-sm text-foreground">{opcao}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              }

              if (campo.tipo === 'textarea') {
                return (
                  <div key={campo.id} className="space-y-2">
                    <label className="block text-sm font-medium text-foreground">
                      {campo.label} {campo.obrigatorio && '*'}
                    </label>
                    <textarea
                      value={(values[campo.id] as string) || ''}
                      onChange={(e) => handleTextChange(campo.id, e.target.value)}
                      placeholder={campo.placeholder}
                      rows={3}
                      className={`w-full px-4 py-3 bg-background/50 border rounded-lg text-foreground placeholder:text-muted-foreground transition-colors resize-none ${
                        erro ? 'border-red-500/50' : 'border-border/50 focus:border-primary'
                      }`}
                    />
                    {erro && (
                      <div className="flex items-center gap-2 text-sm text-red-600">
                        <AlertCircle className="w-4 h-4" />
                        {erro}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <div key={campo.id} className="space-y-2">
                  <label className="block text-sm font-medium text-foreground">
                    {campo.label} {campo.obrigatorio && '*'}
                  </label>
                  <input
                    type="text"
                    value={(values[campo.id] as string) || ''}
                    onChange={(e) => handleTextChange(campo.id, e.target.value)}
                    placeholder={campo.placeholder}
                    className={`w-full px-4 py-3 bg-background/50 border rounded-lg text-foreground placeholder:text-muted-foreground transition-colors ${
                      erro ? 'border-red-500/50' : 'border-border/50 focus:border-primary'
                    }`}
                  />
                  {erro && (
                    <div className="flex items-center gap-2 text-sm text-red-600">
                      <AlertCircle className="w-4 h-4" />
                      {erro}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Seção 12 — Anexos */}
      <div className="space-y-4">
        <div className="border-b border-border/50 pb-2">
          <h3 className="text-lg font-semibold text-foreground">12. Anexos</h3>
          <p className="text-sm text-muted-foreground">Envie os documentos abaixo, se disponíveis</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {camposAnexos.map((campo) => {
            const file = anexos[campo.id];
            return (
              <div key={campo.id} className="space-y-2">
                <label className="block text-sm font-medium text-foreground">{campo.label}</label>
                {file ? (
                  <div className="flex items-center justify-between p-3 bg-background/50 border border-border/50 rounded-lg">
                    <div className="flex items-center gap-2 min-w-0">
                      <Paperclip className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm text-foreground truncate">{file.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleAnexoChange(campo.id, null)}
                      className="p-1 hover:bg-red-500/10 rounded text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 px-3 py-2 bg-background/50 border border-border/50 rounded-lg cursor-pointer hover:border-primary/50 transition-colors">
                    <Paperclip className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Selecionar arquivo</span>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.csv,.zip"
                      onChange={(e) => handleAnexoChange(campo.id, e.target.files?.[0] || null)}
                    />
                  </label>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-4 pt-4 border-t border-border/50">
        <button
          type="submit"
          disabled={submitting || saving}
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Enviar Briefing
            </>
          )}
        </button>

        {onSave && (
          <button
            type="button"
            onClick={handleSave}
            disabled={submitting || saving}
            className="inline-flex items-center gap-2 px-6 py-3 bg-muted/50 text-foreground rounded-lg hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Salvar Rascunho
              </>
            )}
          </button>
        )}
      </div>
    </form>
  );
}
